import { verifyBlobHash } from '@kehto/nip/5d';
import {
  ResourceServiceError,
  type ResourceInfo,
  type ResourceServiceOptions,
} from '@kehto/services';

import { normalizeUploadServers } from './simulation.js';
import { parsePajaBlossomUri } from './browser-blossom-uri.js';
import { warnLegacyBlossomPrefix } from './browser-blossom-compat.js';

/** Maximum decoded payload exposed by Paja's local data-resource backend. */
export const PAJA_RESOURCE_MAX_BYTES = 10 * 1024 * 1024;
/** Maximum URLs accepted by one Paja resource bulk request. */
export const PAJA_RESOURCE_MAX_URLS = 100;
/** Maximum combined request-hint and host-default Blossom servers per resource. */
export const PAJA_RESOURCE_MAX_SERVERS = 8;

const UTF8_DECODER = new TextDecoder('utf-8', { fatal: true });
const GAME_BOY_NINTENDO_LOGO = [
  0xce, 0xed, 0x66, 0x66, 0xcc, 0x0d, 0x00, 0x0b,
  0x03, 0x73, 0x00, 0x83, 0x00, 0x0c, 0x00, 0x0d,
  0x00, 0x08, 0x11, 0x1f, 0x88, 0x89, 0x00, 0x0e,
  0xdc, 0xcc, 0x6e, 0xe6, 0xdd, 0xdd, 0xd9, 0x99,
  0xbb, 0xbb, 0x67, 0x63, 0x6e, 0x0e, 0xec, 0xcc,
  0xdd, 0xdc, 0x99, 0x9f, 0xbb, 0xb9, 0x33, 0x3e,
] as const;
const MVT_MEDIA_TYPE = 'application/vnd.mapbox-vector-tile';
const PROTOBUF_MEDIA_TYPE = 'application/x-protobuf';
const PROTOBUF_WIRE_VARINT = 0;
const PROTOBUF_WIRE_FIXED64 = 1;
const PROTOBUF_WIRE_LENGTH = 2;
const PROTOBUF_WIRE_FIXED32 = 5;
const PROTOBUF_VARINT_MAX_BYTES = 10;
const PROTOBUF_NAME_MAX_BYTES = 1024;
const MVT_TILE_LAYERS = 3;
const MVT_LAYER_NAME = 1;
const MVT_LAYER_NAME_PATTERN = /^[A-Za-z0-9_]{1,64}$/;
const GLYPH_FONTSTACK = 1;
const GLYPH_FONTSTACK_NAME = 1;
const GLYPH_FONTSTACK_RANGE = 2;
const GLYPH_RANGE_PATTERN = /^\d{1,5}-\d{1,5}$/;


/** Host-owned inputs for Paja's NAP-RESOURCE fetch boundary. */
export interface PajaResourceFetchOptions {
  /** Return ordered runtime candidates for this source-scoped Blossom URL. */
  readonly getBlossomServers?: (context: {
    readonly url: string;
    readonly windowId?: string;
  }) => readonly string[] | Promise<readonly string[]>;
  /** Fetch implementation used for configured Blossom server requests. */
  readonly fetch?: typeof fetch;
}

/**
 * Create Paja's developer-oriented NAP-RESOURCE fetch boundary.
 *
 * `data:` bytes are decoded locally. `blossom:<hex>` URLs and the
 * `blossom:sha256:<hex>` compatibility alias are
 * resolved through accepted request hints followed by host-configured Blossom
 * servers, with redirects disabled and SHA-256 verified before delivery.
 * Direct HTTP(S) URLs are resolved from any origin through the browser,
 * subject to its CORS rules. All bytes are capped and classified locally;
 * upstream media types are ignored.
 *
 * @param options - Host-owned Blossom server and transport hooks.
 * @returns A sanitized resource fetch implementation.
 */
export function createPajaResourceFetch(
  options: PajaResourceFetchOptions = {},
): ResourceServiceOptions['fetch'] {
  return async (value, init) => {
    if (init.signal.aborted) throw new DOMException('Resource request cancelled', 'AbortError');
    const url = new URL(value);
    if (init.method && init.method.toUpperCase() !== 'GET') {
      throw new ResourceServiceError('invalid-request', 'NAP-RESOURCE is read-only');
    }
    if (url.protocol === 'blossom:') {
      return fetchBlossomResource(
        value,
        init.signal,
        init.servers ?? [],
        init.windowId,
        options,
      );
    }
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return fetchNetworkResource(value, init.signal, options.fetch ?? globalThis.fetch);
    }
    if (url.protocol !== 'data:') {
      throw new ResourceServiceError('unsupported-scheme', `Paja does not enable ${url.protocol}`);
    }
    if (value.length > encodedDataUrlLimit(PAJA_RESOURCE_MAX_BYTES)) {
      throw new ResourceServiceError('too-large', `resource exceeds ${PAJA_RESOURCE_MAX_BYTES} bytes`);
    }

    const decoded = await fetch(value, { signal: init.signal });
    const bytes = new Uint8Array(await decoded.arrayBuffer());
    if (bytes.byteLength > PAJA_RESOURCE_MAX_BYTES) {
      throw new ResourceServiceError('too-large', `resource exceeds ${PAJA_RESOURCE_MAX_BYTES} bytes`);
    }
    const classification = classifyResourceBytes(bytes);
    if (classification.kind !== 'recognized') {
      throw new ResourceServiceError('decode-failed', 'resource bytes are not an enabled safe media type');
    }
    return new Response(arrayBufferFor(bytes), { headers: { 'content-type': classification.mime } });
  };
}

/**
 * Return Paja's truthful NAP-RESOURCE policy disclosure.
 *
 * @returns Current resource schemes and enforced limits.
 */
export function pajaResourceInfo(): ResourceInfo {
  const schemes = [
    { scheme: 'data', enabled: true },
    { scheme: 'https', enabled: true },
    { scheme: 'http', enabled: true },
    { scheme: 'blossom', enabled: true },
  ];
  return {
    schemes,
    maxBytes: PAJA_RESOURCE_MAX_BYTES,
    maxUrls: PAJA_RESOURCE_MAX_URLS,
    maxServers: PAJA_RESOURCE_MAX_SERVERS,
  };
}

async function fetchNetworkResource(
  value: string,
  signal: AbortSignal,
  fetcher: typeof fetch,
): Promise<Response> {
  let response: Response;
  try {
    response = await fetcher(value, {
      method: 'GET',
      signal,
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
    });
  } catch (error: unknown) {
    if (signal.aborted || isAbortError(error)) throw error;
    throw new ResourceServiceError(
      'network-error',
      error instanceof Error ? error.message : String(error),
    );
  }
  if (response.status === 404 || response.status === 410) {
    throw new ResourceServiceError('not-found', 'HTTP resource was not found');
  }
  if (!response.ok) {
    throw new ResourceServiceError('network-error', `HTTP resource returned ${response.status}`);
  }

  const bytes = await readCappedResponse(response, signal);
  const classification = classifyResourceBytes(bytes);
  if (classification.kind !== 'recognized') {
    throw new ResourceServiceError('decode-failed', 'resource bytes are not an enabled safe media type');
  }
  return new Response(arrayBufferFor(bytes), { headers: { 'content-type': classification.mime } });
}

async function fetchBlossomResource(
  value: string,
  signal: AbortSignal,
  requestServers: readonly string[],
  windowId: string | undefined,
  options: PajaResourceFetchOptions,
): Promise<Response> {
  const parsed = parsePajaBlossomUri(value);
  if (!parsed) {
    throw new ResourceServiceError('invalid-request', 'invalid Blossom URI');
  }
  warnLegacyBlossomPrefix(value);
  if (parsed.expectedSize !== undefined && parsed.expectedSize > PAJA_RESOURCE_MAX_BYTES) throw resourceTooLarge();
  const expectedHash = parsed.hash;
  const configuredServers = await options.getBlossomServers?.({ url: value, windowId }) ?? [];
  if (signal.aborted) throw new DOMException('Resource request cancelled', 'AbortError');
  const uriServers = parsed.servers.flatMap((value) => {
    const server = normalizeBlossomUriServer(value);
    return server ? [server] : [];
  });
  const servers = resolveBlossomServers([...requestServers, ...uriServers], configuredServers);
  if (servers.length === 0) {
    throw new ResourceServiceError('blocked-by-policy', 'Paja has no accepted Blossom server');
  }

  const fetcher = options.fetch ?? globalThis.fetch;
  let foundHashMismatch = false;
  let foundNotFound = false;
  let foundInconclusive = false;
  let lastNetworkMessage = 'all accepted Blossom servers failed';
  for (const server of servers) {
    if (signal.aborted) throw new DOMException('Resource request cancelled', 'AbortError');
    const resourceUrl = `${server}/${expectedHash}${parsed.extension ? `.${parsed.extension}` : ''}`;
    let response: Response;
    try {
      response = await fetcher(resourceUrl, {
        method: 'GET',
        signal,
        redirect: 'error',
        cache: 'no-store',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
      });
    } catch (error: unknown) {
      if (signal.aborted || isAbortError(error)) throw error;
      foundInconclusive = true;
      lastNetworkMessage = error instanceof Error ? error.message : String(error);
      continue;
    }
    if (response.status === 404 || response.status === 410) {
      foundNotFound = true;
      continue;
    }
    if (!response.ok) {
      foundInconclusive = true;
      lastNetworkMessage = `Blossom server returned HTTP ${response.status}`;
      continue;
    }

    const declared = response.headers.get('content-length');
    if (declared !== null && Number(declared) > PAJA_RESOURCE_MAX_BYTES) throw resourceTooLarge();
    if (parsed.expectedSize !== undefined && declared !== null && /^[0-9]+$/u.test(declared)
      && Number(declared) !== parsed.expectedSize) {
      await response.body?.cancel();
      foundHashMismatch = true;
      continue;
    }
    const bytes = await readCappedResponse(response, signal);
    if ((parsed.expectedSize !== undefined && bytes.byteLength !== parsed.expectedSize)
      || !verifyBlobHash(bytes, expectedHash)) {
      foundHashMismatch = true;
      continue;
    }
    const classification = classifyResourceBytes(bytes);
    if (classification.kind === 'blocked') {
      throw new ResourceServiceError('decode-failed', 'resource bytes are not an enabled safe media type');
    }
    const mime = classification.kind === 'recognized' ? classification.mime : 'application/octet-stream';
    return new Response(arrayBufferFor(bytes), { headers: { 'content-type': mime } });
  }

  if (foundHashMismatch) {
    throw new ResourceServiceError('decode-failed', 'Blossom response did not match the requested SHA-256 or size');
  }
  if (foundInconclusive) {
    throw new ResourceServiceError('network-error', lastNetworkMessage);
  }
  if (foundNotFound) {
    throw new ResourceServiceError('not-found', 'Blossom resource was not found');
  }
  throw new ResourceServiceError('network-error', lastNetworkMessage);
}

function resolveBlossomServers(
  requestServers: readonly string[],
  configuredServers: readonly string[],
): string[] {
  const servers: string[] = [];
  for (const value of requestServers) {
    const server = normalizePublicBlossomServer(value);
    if (server && !servers.includes(server)) servers.push(server);
    if (servers.length === PAJA_RESOURCE_MAX_SERVERS) return servers;
  }
  for (const server of usableBlossomServers(configuredServers)) {
    if (!servers.includes(server)) servers.push(server);
    if (servers.length === PAJA_RESOURCE_MAX_SERVERS) break;
  }
  return servers;
}

/** Normalize an untrusted Blossom hint to one public HTTPS origin. */
export function normalizeBlossomUriServer(value: string): string | null {
  if (/^[a-z0-9.-]+(?::\d+)?$/i.test(value)) {
    return normalizePublicBlossomServer(`https://${value}`);
  }
  return normalizePublicBlossomServer(value);
}

/** Normalize an untrusted explicit server to one public HTTPS origin. */
export function normalizePublicBlossomServer(value: string): string | null {
  if (typeof value !== 'string' || value.trim().length === 0) return null;
  try {
    const url = new URL(value.trim());
    if (
      url.protocol !== 'https:'
      || url.username
      || url.password
      || url.search
      || url.hash
      || url.pathname !== '/'
      || !isPublicHostname(url.hostname)
    ) return null;
    return url.origin;
  } catch {
    return null;
  }
}

// Paja is a browser-only developer runtime: reject obvious local/private
// literals here, while production resolvers must additionally enforce the
// NAP-RESOURCE DNS-time address check before connecting and after redirects.
function isPublicHostname(value: string): boolean {
  const hostname = value.replace(/^\[|\]$/gu, '').toLowerCase();
  if (
    hostname === 'localhost'
    || hostname.endsWith('.localhost')
    || hostname.endsWith('.local')
    || hostname.endsWith('.internal')
    || !hostname.includes('.')
  ) return false;
  if (hostname.includes(':')) {
    return hostname !== '::'
      && hostname !== '::1'
      && !hostname.startsWith('::ffff:')
      && !/^(?:fc|fd|fe[89ab]|ff)/iu.test(hostname)
      && !hostname.startsWith('2001:db8:');
  }
  const octets = hostname.split('.').map(Number);
  if (octets.length !== 4 || octets.some((octet) => !Number.isInteger(octet) || octet < 0 || octet > 255)) return true;
  const [first, second] = octets as [number, number, number, number];
  return first !== 0
    && first !== 10
    && first !== 127
    && first < 224
    && !(first === 100 && second >= 64 && second <= 127)
    && !(first === 169 && second === 254)
    && !(first === 172 && second >= 16 && second <= 31)
    && !(first === 192 && second === 168)
    && !(first === 198 && (second === 18 || second === 19));
}

function usableBlossomServers(values: readonly string[]): string[] {
  const servers: string[] = [];
  for (const value of values) {
    try {
      const [server] = normalizeUploadServers([value]);
      if (server && !servers.includes(server)) servers.push(server);
    } catch {
      // Host config can contain stale or untrusted discovery hints. Ignore them.
    }
  }
  return servers;
}

async function readCappedResponse(response: Response, signal: AbortSignal): Promise<Uint8Array> {
  const declaredLength = Number(response.headers.get('content-length'));
  if (Number.isFinite(declaredLength) && declaredLength > PAJA_RESOURCE_MAX_BYTES) {
    throw resourceTooLarge();
  }
  if (!response.body) {
    const bytes = new Uint8Array(await response.arrayBuffer());
    if (bytes.byteLength > PAJA_RESOURCE_MAX_BYTES) throw resourceTooLarge();
    return bytes;
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    while (true) {
      if (signal.aborted) throw new DOMException('Resource request cancelled', 'AbortError');
      const { done, value } = await reader.read();
      if (done) break;
      total += value.byteLength;
      if (total > PAJA_RESOURCE_MAX_BYTES) {
        await reader.cancel();
        throw resourceTooLarge();
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }

  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return bytes;
}

function resourceTooLarge(): ResourceServiceError {
  return new ResourceServiceError('too-large', `resource exceeds ${PAJA_RESOURCE_MAX_BYTES} bytes`);
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && (error.name === 'AbortError' || error.name === 'DOMException');
}

function arrayBufferFor(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

function encodedDataUrlLimit(maxBytes: number): number {
  return Math.ceil(maxBytes * 4 / 3) + 1024;
}

function startsWith(bytes: Uint8Array, signature: readonly number[], offset = 0): boolean {
  return signature.every((value, index) => bytes[offset + index] === value);
}

function ascii(bytes: Uint8Array, start: number, end: number): string {
  return String.fromCharCode(...bytes.subarray(start, end));
}

type ResourceClassification =
  | { kind: 'recognized'; mime: string }
  | { kind: 'opaque' }
  | { kind: 'blocked' };

function classifyResourceBytes(bytes: Uint8Array): ResourceClassification {
  const mime = sniffBinaryMime(bytes);
  if (mime) return { kind: 'recognized', mime };
  return classifyTextBytes(bytes);
}

function sniffBinaryMime(bytes: Uint8Array): string | null {
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png';
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) return 'image/jpeg';
  if (ascii(bytes, 0, 6) === 'GIF87a' || ascii(bytes, 0, 6) === 'GIF89a') return 'image/gif';
  if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 12) === 'WEBP') return 'image/webp';
  if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 12) === 'WAVE') return 'audio/wav';
  if (ascii(bytes, 0, 4) === 'OggS') return 'audio/ogg';
  if (ascii(bytes, 0, 3) === 'ID3' || startsWith(bytes, [0xff, 0xfb])) return 'audio/mpeg';
  if (startsWith(bytes, [0x1a, 0x45, 0xdf, 0xa3])) return 'video/webm';
  if (ascii(bytes, 4, 8) === 'ftyp') return 'video/mp4';
  if (ascii(bytes, 0, 4) === 'wOFF') return 'font/woff';
  if (ascii(bytes, 0, 4) === 'wOF2') return 'font/woff2';
  if (isGameBoyRom(bytes)) return 'application/vnd.nintendo.gb-rom';
  // Protobuf carries no signature, so it is validated structurally instead. This
  // runs before the text fallback, and cannot swallow active content: every
  // `<`-prefixed document (SVG, XML, HTML, script) starts with 0x3c, whose low
  // three bits are an invalid (group) wire type.
  const protobuf = sniffProtobufMime(bytes);
  if (protobuf) return protobuf;
  return null;
}

function isGameBoyRom(bytes: Uint8Array): boolean {
  if (bytes.byteLength < 0x150 || !startsWith(bytes, GAME_BOY_NINTENDO_LOGO, 0x104)) return false;
  let checksum = 0;
  for (let index = 0x134; index <= 0x14c; index += 1) {
    checksum = (checksum - (bytes[index] ?? 0) - 1) & 0xff;
  }
  return checksum === bytes[0x14d];
}

interface ProtobufField {
  readonly tag: number;
  readonly wire: number;
  readonly payload?: Uint8Array;
}

function readVarint(bytes: Uint8Array, offset: number): { value: number; next: number } | null {
  let value = 0;
  let shift = 0;
  let index = offset;
  while (index < bytes.byteLength && index - offset < PROTOBUF_VARINT_MAX_BYTES) {
    const byte = bytes[index] as number;
    index += 1;
    value += (byte & 0x7f) * 2 ** shift;
    if ((byte & 0x80) === 0) {
      return Number.isSafeInteger(value) ? { value, next: index } : null;
    }
    shift += 7;
  }
  return null;
}

function readProtobufFields(bytes: Uint8Array): ProtobufField[] | null {
  const fields: ProtobufField[] = [];
  let offset = 0;
  while (offset < bytes.byteLength) {
    const key = readVarint(bytes, offset);
    if (!key) return null;
    const tag = Math.floor(key.value / 8);
    const wire = key.value % 8;
    if (tag === 0) return null;
    offset = key.next;
    if (wire === PROTOBUF_WIRE_VARINT) {
      const scalar = readVarint(bytes, offset);
      if (!scalar) return null;
      offset = scalar.next;
      fields.push({ tag, wire });
      continue;
    }
    if (wire === PROTOBUF_WIRE_LENGTH) {
      const length = readVarint(bytes, offset);
      if (!length) return null;
      const end = length.next + length.value;
      if (end > bytes.byteLength) return null;
      fields.push({ tag, wire, payload: bytes.subarray(length.next, end) });
      offset = end;
      continue;
    }
    if (wire === PROTOBUF_WIRE_FIXED32) {
      if (offset + 4 > bytes.byteLength) return null;
      offset += 4;
      fields.push({ tag, wire });
      continue;
    }
    if (wire === PROTOBUF_WIRE_FIXED64) {
      if (offset + 8 > bytes.byteLength) return null;
      offset += 8;
      fields.push({ tag, wire });
      continue;
    }
    return null;
  }
  return fields.length > 0 ? fields : null;
}

function protobufPayload(fields: readonly ProtobufField[], tag: number): Uint8Array | null {
  for (const field of fields) {
    if (field.tag === tag && field.wire === PROTOBUF_WIRE_LENGTH && field.payload) return field.payload;
  }
  return null;
}

function decodeResourceName(payload: Uint8Array): string | null {
  if (payload.byteLength === 0 || payload.byteLength > PROTOBUF_NAME_MAX_BYTES) return null;
  try {
    return UTF8_DECODER.decode(payload);
  } catch {
    return null;
  }
}

function isPlausibleResourceName(payload: Uint8Array): boolean {
  const name = decodeResourceName(payload);
  return name !== null && name.trim() === name && !/[\u0000-\u001f\u007f]/u.test(name);
}

function isMapboxVectorTile(fields: readonly ProtobufField[]): boolean {
  if (!fields.every((field) => field.tag === MVT_TILE_LAYERS && field.wire === PROTOBUF_WIRE_LENGTH)) {
    return false;
  }
  return fields.every((field) => {
    const layer = readProtobufFields(field.payload as Uint8Array);
    if (!layer) return false;
    const name = decodeResourceName(protobufPayload(layer, MVT_LAYER_NAME) ?? new Uint8Array());
    return name !== null && MVT_LAYER_NAME_PATTERN.test(name);
  });
}

function isMapboxGlyphRange(fields: readonly ProtobufField[]): boolean {
  if (!fields.every((field) => field.tag === GLYPH_FONTSTACK && field.wire === PROTOBUF_WIRE_LENGTH)) {
    return false;
  }
  return fields.every((field) => {
    const fontstack = readProtobufFields(field.payload as Uint8Array);
    if (!fontstack) return false;
    const name = protobufPayload(fontstack, GLYPH_FONTSTACK_NAME);
    const range = decodeResourceName(protobufPayload(fontstack, GLYPH_FONTSTACK_RANGE) ?? new Uint8Array());
    return name !== null && isPlausibleResourceName(name) && range !== null && GLYPH_RANGE_PATTERN.test(range);
  });
}

function sniffProtobufMime(bytes: Uint8Array): string | null {
  const fields = readProtobufFields(bytes);
  if (!fields) return null;
  if (isMapboxVectorTile(fields)) return MVT_MEDIA_TYPE;
  if (isMapboxGlyphRange(fields)) return PROTOBUF_MEDIA_TYPE;
  const hasPayload = fields.some((field) => (field.payload?.byteLength ?? 0) > 0);
  return hasPayload ? PROTOBUF_MEDIA_TYPE : null;
}

function classifyTextBytes(bytes: Uint8Array): ResourceClassification {
  // Inspect markup before strict decoding: invalid UTF-8 or NUL later in an
  // active document must not turn a blocked prefix into opaque Blossom bytes.
  const normalized = new TextDecoder().decode(bytes).trimStart().toLowerCase();
  if (hasBlockedMarkupPrefix(normalized) || hasBlockedEncodedMarkup(bytes)) return { kind: 'blocked' };
  let text: string;
  try {
    text = UTF8_DECODER.decode(bytes);
  } catch {
    return { kind: 'opaque' };
  }
  if (/\u0000/u.test(text)) return { kind: 'opaque' };
  if (normalized.startsWith('{') || normalized.startsWith('[')) {
    try {
      JSON.parse(text);
      return { kind: 'recognized', mime: 'application/json' };
    } catch {
      return { kind: 'recognized', mime: 'text/plain' };
    }
  }
  return { kind: 'recognized', mime: 'text/plain' };
}

function hasBlockedMarkupPrefix(text: string): boolean {
  const normalized = text.trimStart().toLowerCase();
  return (
    normalized.startsWith('<svg')
    || normalized.startsWith('<?xml')
    || normalized.startsWith('<!doctype html')
    || normalized.startsWith('<html')
    || normalized.startsWith('<script')
  );
}

function hasBlockedEncodedMarkup(bytes: Uint8Array): boolean {
  // Conservatively reject identifiable UTF-32 documents; no UTF-32 media
  // format is enabled. Check these before the overlapping UTF-16LE BOM.
  if (
    startsWith(bytes, [0xff, 0xfe, 0x00, 0x00])
    || startsWith(bytes, [0x00, 0x00, 0xfe, 0xff])
    || startsWith(bytes, [0x3c, 0x00, 0x00, 0x00])
    || startsWith(bytes, [0x00, 0x00, 0x00, 0x3c])
  ) return true;

  let encoding: string;
  if (startsWith(bytes, [0xff, 0xfe]) || startsWith(bytes, [0x3c, 0x00])) {
    encoding = 'utf-16le';
  } else if (startsWith(bytes, [0xfe, 0xff]) || startsWith(bytes, [0x00, 0x3c])) {
    encoding = 'utf-16be';
  } else {
    return false;
  }
  return hasBlockedMarkupPrefix(new TextDecoder(encoding).decode(bytes));
}
